// Enhanced validation system for reading tasks and writing assessment.
// Grammar fairness rules: see src/data/tasks/ANSWER_DESIGN.md

import { dictionaryService } from '../services/dictionaryService';

export interface ValidationResult {
  isCorrect: boolean;
  confidence: number; // 0-1 scale
  suggestions: string[];
  feedback: string;
  partialCredit?: number; // 0-1 scale for partial credit
  correctAnswer?: string; // The correct answer for display
  explanation?: string; // Optional explanation for learning
}

// Enhanced word embeddings for reading comprehension (in a real app, you'd use a proper embedding model)
const WORD_EMBEDDINGS: { [key: string]: number[] } = {
  // Basic emotions and states
  'happy': [1, 0.8, 0.2, 0.1],
  'joyful': [0.9, 0.8, 0.3, 0.1],
  'cheerful': [0.8, 0.7, 0.2, 0.1],
  'glad': [0.7, 0.6, 0.1, 0.1],
  'sad': [-0.8, -0.7, -0.3, -0.2],
  'sorrowful': [-0.9, -0.8, -0.4, -0.2],
  'unhappy': [-0.7, -0.6, -0.2, -0.1],
  'angry': [-0.6, -0.8, 0.1, 0.3],
  'mad': [-0.5, -0.7, 0.1, 0.2],
  'furious': [-0.8, -0.9, 0.2, 0.4],
  'nervous': [-0.3, -0.5, 0.1, 0.2],
  'dreadfully': [-0.7, -0.8, 0.1, 0.3],
  'dreadful': [-0.6, -0.7, 0.1, 0.2],

  // Senses and perception
  'hearing': [0.1, 0.9, 0.1, 0.1],
  'sense of hearing': [0.1, 0.9, 0.1, 0.1],
  'auditory': [0.1, 0.9, 0.1, 0.1],
  'sound': [0.1, 0.8, 0.1, 0.1],
  'heard': [0.1, 0.8, 0.1, 0.1],
  'hear': [0.1, 0.8, 0.1, 0.1],
  'sight': [0.1, 0.1, 0.9, 0.1],
  'vision': [0.1, 0.1, 0.9, 0.1],
  'seeing': [0.1, 0.1, 0.8, 0.1],
  'see': [0.1, 0.1, 0.8, 0.1],
  'saw': [0.1, 0.1, 0.8, 0.1],
  'eye': [0.1, 0.1, 0.9, 0.1],
  'eyes': [0.1, 0.1, 0.9, 0.1],
  'touch': [0.1, 0.1, 0.1, 0.9],
  'feeling': [0.1, 0.1, 0.1, 0.8],
  'tactile': [0.1, 0.1, 0.1, 0.9],

  // Reading comprehension common words
  'narrator': [0.1, 0.1, 0.1, 0.1],
  'character': [0.1, 0.1, 0.1, 0.1],
  'story': [0.1, 0.1, 0.1, 0.1],
  'tale': [0.1, 0.1, 0.1, 0.1],
  'passage': [0.1, 0.1, 0.1, 0.1],
  'text': [0.1, 0.1, 0.1, 0.1],
  'excerpt': [0.1, 0.1, 0.1, 0.1],
  'author': [0.1, 0.1, 0.1, 0.1],
  'writer': [0.1, 0.1, 0.1, 0.1],
  'poet': [0.1, 0.1, 0.1, 0.1],

  // Common reading comprehension phrases
  'the narrator': [0.1, 0.1, 0.1, 0.1],
  'the character': [0.1, 0.1, 0.1, 0.1],
  'the author': [0.1, 0.1, 0.1, 0.1],
  'the writer': [0.1, 0.1, 0.1, 0.1],
  'in the story': [0.1, 0.1, 0.1, 0.1],
  'in the text': [0.1, 0.1, 0.1, 0.1],
  'in the passage': [0.1, 0.1, 0.1, 0.1],
  'according to': [0.1, 0.1, 0.1, 0.1],
  'the text says': [0.1, 0.1, 0.1, 0.1],
  'the passage states': [0.1, 0.1, 0.1, 0.1],
  'it says': [0.1, 0.1, 0.1, 0.1],
  'it states': [0.1, 0.1, 0.1, 0.1],
  'it mentions': [0.1, 0.1, 0.1, 0.1],

  // Question words and phrases
  'what': [0.1, 0.1, 0.1, 0.1],
  'who': [0.1, 0.1, 0.1, 0.1],
  'when': [0.1, 0.1, 0.1, 0.1],
  'where': [0.1, 0.1, 0.1, 0.1],
  'why': [0.1, 0.1, 0.1, 0.1],
  'how': [0.1, 0.1, 0.1, 0.1],
  'which': [0.1, 0.1, 0.1, 0.1],
  'describe': [0.1, 0.1, 0.1, 0.1],
  'explain': [0.1, 0.1, 0.1, 0.1],
  'summarize': [0.1, 0.1, 0.1, 0.1],

  // Common descriptive words
  'old': [0.1, 0.1, 0.1, 0.1],
  'young': [0.1, 0.1, 0.1, 0.1],
  'man': [0.1, 0.1, 0.1, 0.1],
  'woman': [0.1, 0.1, 0.1, 0.1],
  'person': [0.1, 0.1, 0.1, 0.1],
  'people': [0.1, 0.1, 0.1, 0.1],
  'thing': [0.1, 0.1, 0.1, 0.1],
  'things': [0.1, 0.1, 0.1, 0.1],
  'reason': [0.1, 0.1, 0.1, 0.1],
  'reasons': [0.1, 0.1, 0.1, 0.1],
  'idea': [0.1, 0.1, 0.1, 0.1],
  'ideas': [0.1, 0.1, 0.1, 0.1],
  'thought': [0.1, 0.1, 0.1, 0.1],
  'thoughts': [0.1, 0.1, 0.1, 0.1],

  // Common verbs in reading comprehension
  'said': [0.1, 0.1, 0.1, 0.1],
  'says': [0.1, 0.1, 0.1, 0.1],
  'told': [0.1, 0.1, 0.1, 0.1],
  'tells': [0.1, 0.1, 0.1, 0.1],
  'explained': [0.1, 0.1, 0.1, 0.1],
  'explains': [0.1, 0.1, 0.1, 0.1],
  'described': [0.1, 0.1, 0.1, 0.1],
  'describes': [0.1, 0.1, 0.1, 0.1],
  'mentioned': [0.1, 0.1, 0.1, 0.1],
  'mentions': [0.1, 0.1, 0.1, 0.1],
  'stated': [0.1, 0.1, 0.1, 0.1],
  'states': [0.1, 0.1, 0.1, 0.1],
  'wrote': [0.1, 0.1, 0.1, 0.1],
  'writes': [0.1, 0.1, 0.1, 0.1],

  // Common adjectives
  'most': [0.1, 0.1, 0.1, 0.1],
  'more': [0.1, 0.1, 0.1, 0.1],
  'less': [0.1, 0.1, 0.1, 0.1],
  'least': [0.1, 0.1, 0.1, 0.1],
  'best': [0.1, 0.1, 0.1, 0.1],
  'worst': [0.1, 0.1, 0.1, 0.1],
  'better': [0.1, 0.1, 0.1, 0.1],
  'worse': [0.1, 0.1, 0.1, 0.1],
  'important': [0.1, 0.1, 0.1, 0.1],
  'main': [0.1, 0.1, 0.1, 0.1],
  'primary': [0.1, 0.1, 0.1, 0.1],
  'key': [0.1, 0.1, 0.1, 0.1],
  'central': [0.1, 0.1, 0.1, 0.1],

  // Common synonyms
  'big': [0.1, 0.1, 0.1, 0.1],
  'large': [0.1, 0.1, 0.1, 0.1],
  'huge': [0.1, 0.1, 0.1, 0.1],
  'enormous': [0.1, 0.1, 0.1, 0.1],
  'small': [0.1, 0.1, 0.1, 0.1],
  'little': [0.1, 0.1, 0.1, 0.1],
  'tiny': [0.1, 0.1, 0.1, 0.1],
  'miniature': [0.1, 0.1, 0.1, 0.1],

  // Actions
  'run': [0.1, 0.1, 0.1, 0.1],
  'running': [0.1, 0.1, 0.1, 0.1],
  'walk': [0.1, 0.1, 0.1, 0.1],
  'walking': [0.1, 0.1, 0.1, 0.1],
  'jump': [0.1, 0.1, 0.1, 0.1],
  'jumping': [0.1, 0.1, 0.1, 0.1],
  'fly': [0.1, 0.1, 0.1, 0.1],
  'flying': [0.1, 0.1, 0.1, 0.1],
  'swim': [0.1, 0.1, 0.1, 0.1],
  'swimming': [0.1, 0.1, 0.1, 0.1],
  'climb': [0.1, 0.1, 0.1, 0.1],
  'climbing': [0.1, 0.1, 0.1, 0.1],
  'dance': [0.1, 0.1, 0.1, 0.1],
  'dancing': [0.1, 0.1, 0.1, 0.1],
  'sing': [0.1, 0.1, 0.1, 0.1],
  'singing': [0.1, 0.1, 0.1, 0.1],
  'play': [0.1, 0.1, 0.1, 0.1],
  'playing': [0.1, 0.1, 0.1, 0.1],
  'read': [0.1, 0.1, 0.1, 0.1],
  'reading': [0.1, 0.1, 0.1, 0.1],
  'write': [0.1, 0.1, 0.1, 0.1],
  'writing': [0.1, 0.1, 0.1, 0.1],
  'draw': [0.1, 0.1, 0.1, 0.1],
  'drawing': [0.1, 0.1, 0.1, 0.1],
  'paint': [0.1, 0.1, 0.1, 0.1],
  'painting': [0.1, 0.1, 0.1, 0.1],
  'cook': [0.1, 0.1, 0.1, 0.1],
  'cooking': [0.1, 0.1, 0.1, 0.1],
  'eat': [0.1, 0.1, 0.1, 0.1],
  'eating': [0.1, 0.1, 0.1, 0.1],
  'drink': [0.1, 0.1, 0.1, 0.1],
  'drinking': [0.1, 0.1, 0.1, 0.1],
  'sleep': [0.1, 0.1, 0.1, 0.1],
  'sleeping': [0.1, 0.1, 0.1, 0.1],
  'work': [0.1, 0.1, 0.1, 0.1],
  'working': [0.1, 0.1, 0.1, 0.1],
  'study': [0.1, 0.1, 0.1, 0.1],
  'studying': [0.1, 0.1, 0.1, 0.1],
  'learn': [0.1, 0.1, 0.1, 0.1],
  'learning': [0.1, 0.1, 0.1, 0.1],
  'teach': [0.1, 0.1, 0.1, 0.1],
  'teaching': [0.1, 0.1, 0.1, 0.1],
  'help': [0.1, 0.1, 0.1, 0.1],
  'helping': [0.1, 0.1, 0.1, 0.1],
  'build': [0.1, 0.1, 0.1, 0.1],
  'building': [0.1, 0.1, 0.1, 0.1],
  'create': [0.1, 0.1, 0.1, 0.1],
  'creating': [0.1, 0.1, 0.1, 0.1],
  'make': [0.1, 0.1, 0.1, 0.1],
  'making': [0.1, 0.1, 0.1, 0.1],
  'fix': [0.1, 0.1, 0.1, 0.1],
  'fixing': [0.1, 0.1, 0.1, 0.1],
  'repair': [0.1, 0.1, 0.1, 0.1],
  'repairing': [0.1, 0.1, 0.1, 0.1],
  'clean': [0.1, 0.1, 0.1, 0.1],
  'cleaning': [0.1, 0.1, 0.1, 0.1],
  'wash': [0.1, 0.1, 0.1, 0.1],
  'washing': [0.1, 0.1, 0.1, 0.1],
  'drive': [0.1, 0.1, 0.1, 0.1],
  'driving': [0.1, 0.1, 0.1, 0.1],
  'ride': [0.1, 0.1, 0.1, 0.1],
  'riding': [0.1, 0.1, 0.1, 0.1],
  'travel': [0.1, 0.1, 0.1, 0.1],
  'traveling': [0.1, 0.1, 0.1, 0.1],
  'visit': [0.1, 0.1, 0.1, 0.1],
  'visiting': [0.1, 0.1, 0.1, 0.1],
  'explore': [0.1, 0.1, 0.1, 0.1],
  'exploring': [0.1, 0.1, 0.1, 0.1],
  'discover': [0.1, 0.1, 0.1, 0.1],
  'discovering': [0.1, 0.1, 0.1, 0.1],
  'find': [0.1, 0.1, 0.1, 0.1],
  'finding': [0.1, 0.1, 0.1, 0.1],
  'search': [0.1, 0.1, 0.1, 0.1],
  'searching': [0.1, 0.1, 0.1, 0.1],
  'look': [0.1, 0.1, 0.1, 0.1],
  'looking': [0.1, 0.1, 0.1, 0.1],
  'watch': [0.1, 0.1, 0.1, 0.1],
  'watching': [0.1, 0.1, 0.1, 0.1],
  'listen': [0.1, 0.1, 0.1, 0.1],
  'listening': [0.1, 0.1, 0.1, 0.1],
  'speak': [0.1, 0.1, 0.1, 0.1],
  'speaking': [0.1, 0.1, 0.1, 0.1],
  'talk': [0.1, 0.1, 0.1, 0.1],
  'talking': [0.1, 0.1, 0.1, 0.1],
  'call': [0.1, 0.1, 0.1, 0.1],
  'calling': [0.1, 0.1, 0.1, 0.1],
  'phone': [0.1, 0.1, 0.1, 0.1],
  'phoning': [0.1, 0.1, 0.1, 0.1],
  'texting': [0.1, 0.1, 0.1, 0.1],
  'email': [0.1, 0.1, 0.1, 0.1],
  'emailing': [0.1, 0.1, 0.1, 0.1],
  'meet': [0.1, 0.1, 0.1, 0.1],
  'meeting': [0.1, 0.1, 0.1, 0.1],
  'join': [0.1, 0.1, 0.1, 0.1],
  'joining': [0.1, 0.1, 0.1, 0.1],
  'leave': [0.1, 0.1, 0.1, 0.1],
  'leaving': [0.1, 0.1, 0.1, 0.1],
  'go': [0.1, 0.1, 0.1, 0.1],
  'going': [0.1, 0.1, 0.1, 0.1],
  'come': [0.1, 0.1, 0.1, 0.1],
  'coming': [0.1, 0.1, 0.1, 0.1],
  'arrive': [0.1, 0.1, 0.1, 0.1],
  'arriving': [0.1, 0.1, 0.1, 0.1],
  'depart': [0.1, 0.1, 0.1, 0.1],
  'departing': [0.1, 0.1, 0.1, 0.1],
  'start': [0.1, 0.1, 0.1, 0.1],
  'starting': [0.1, 0.1, 0.1, 0.1],
  'begin': [0.1, 0.1, 0.1, 0.1],
  'beginning': [0.1, 0.1, 0.1, 0.1],
  'end': [0.1, 0.1, 0.1, 0.1],
  'ending': [0.1, 0.1, 0.1, 0.1],
  'finish': [0.1, 0.1, 0.1, 0.1],
  'finishing': [0.1, 0.1, 0.1, 0.1],
  'stop': [0.1, 0.1, 0.1, 0.1],
  'stopping': [0.1, 0.1, 0.1, 0.1],
  'pause': [0.1, 0.1, 0.1, 0.1],
  'pausing': [0.1, 0.1, 0.1, 0.1],
  'wait': [0.1, 0.1, 0.1, 0.1],
  'waiting': [0.1, 0.1, 0.1, 0.1],
  'hurry': [0.1, 0.1, 0.1, 0.1],
  'hurrying': [0.1, 0.1, 0.1, 0.1],
  'rush': [0.1, 0.1, 0.1, 0.1],
  'rushing': [0.1, 0.1, 0.1, 0.1],
  'slow': [0.1, 0.1, 0.1, 0.1],
  'slowing': [0.1, 0.1, 0.1, 0.1],
  'fast': [0.1, 0.1, 0.1, 0.1],
  'quick': [0.1, 0.1, 0.1, 0.1],
  'quickly': [0.1, 0.1, 0.1, 0.1],
  'slowly': [0.1, 0.1, 0.1, 0.1],
  'carefully': [0.1, 0.1, 0.1, 0.1],
  'safely': [0.1, 0.1, 0.1, 0.1],
  'dangerously': [0.1, 0.1, 0.1, 0.1],
  'easily': [0.1, 0.1, 0.1, 0.1],
  'difficultly': [0.1, 0.1, 0.1, 0.1],
  'hard': [0.1, 0.1, 0.1, 0.1],
  'hardly': [0.1, 0.1, 0.1, 0.1],
  'easy': [0.1, 0.1, 0.1, 0.1],
  'difficult': [0.1, 0.1, 0.1, 0.1],
  'simple': [0.1, 0.1, 0.1, 0.1],
  'simply': [0.1, 0.1, 0.1, 0.1],
  'complex': [0.1, 0.1, 0.1, 0.1],
  'complicated': [0.1, 0.1, 0.1, 0.1],
  'basic': [0.1, 0.1, 0.1, 0.1],
  'basically': [0.1, 0.1, 0.1, 0.1],
  'advanced': [0.1, 0.1, 0.1, 0.1],
  'expert': [0.1, 0.1, 0.1, 0.1],
  'professional': [0.1, 0.1, 0.1, 0.1],
  'amateur': [0.1, 0.1, 0.1, 0.1],
  'beginner': [0.1, 0.1, 0.1, 0.1],
  'novice': [0.1, 0.1, 0.1, 0.1],
  'experienced': [0.1, 0.1, 0.1, 0.1],
  'skilled': [0.1, 0.1, 0.1, 0.1],
  'talented': [0.1, 0.1, 0.1, 0.1],
  'gifted': [0.1, 0.1, 0.1, 0.1],
  'natural': [0.1, 0.1, 0.1, 0.1],
  'naturally': [0.1, 0.1, 0.1, 0.1],
  'artificial': [0.1, 0.1, 0.1, 0.1],
  'artificially': [0.1, 0.1, 0.1, 0.1],
  'real': [0.1, 0.1, 0.1, 0.1],
  'really': [0.1, 0.1, 0.1, 0.1],
  'fake': [0.1, 0.1, 0.1, 0.1],
  'false': [0.1, 0.1, 0.1, 0.1],
  'true': [0.1, 0.1, 0.1, 0.1],
  'truly': [0.1, 0.1, 0.1, 0.1],
  'honest': [0.1, 0.1, 0.1, 0.1],
  'honestly': [0.1, 0.1, 0.1, 0.1],
  'dishonest': [0.1, 0.1, 0.1, 0.1],
  'lie': [0.1, 0.1, 0.1, 0.1],
  'lying': [0.1, 0.1, 0.1, 0.1],
  'truth': [0.1, 0.1, 0.1, 0.1],
  'fact': [0.1, 0.1, 0.1, 0.1],
  'facts': [0.1, 0.1, 0.1, 0.1],
  'fiction': [0.1, 0.1, 0.1, 0.1],
  'stories': [0.1, 0.1, 0.1, 0.1],
  'tales': [0.1, 0.1, 0.1, 0.1],
  'myth': [0.1, 0.1, 0.1, 0.1],
  'myths': [0.1, 0.1, 0.1, 0.1],
  'legend': [0.1, 0.1, 0.1, 0.1],
  'legends': [0.1, 0.1, 0.1, 0.1],
  'fable': [0.1, 0.1, 0.1, 0.1],
  'fables': [0.1, 0.1, 0.1, 0.1],
  'fairytale': [0.1, 0.1, 0.1, 0.1],
  'fairytales': [0.1, 0.1, 0.1, 0.1],
  'adventure': [0.1, 0.1, 0.1, 0.1],
  'adventures': [0.1, 0.1, 0.1, 0.1],
  'journey': [0.1, 0.1, 0.1, 0.1],
  'journeys': [0.1, 0.1, 0.1, 0.1],
  'trip': [0.1, 0.1, 0.1, 0.1],
  'trips': [0.1, 0.1, 0.1, 0.1],
  'vacation': [0.1, 0.1, 0.1, 0.1],
  'vacations': [0.1, 0.1, 0.1, 0.1],
  'holiday': [0.1, 0.1, 0.1, 0.1],
  'holidays': [0.1, 0.1, 0.1, 0.1],
  'break': [0.1, 0.1, 0.1, 0.1],
  'breaks': [0.1, 0.1, 0.1, 0.1],
  'rest': [0.1, 0.1, 0.1, 0.1],
  'resting': [0.1, 0.1, 0.1, 0.1],
  'relax': [0.1, 0.1, 0.1, 0.1],
  'relaxing': [0.1, 0.1, 0.1, 0.1],
  'calm': [0.1, 0.1, 0.1, 0.1],
  'calmly': [0.1, 0.1, 0.1, 0.1],
  'peaceful': [0.1, 0.1, 0.1, 0.1],
  'peacefully': [0.1, 0.1, 0.1, 0.1],
  'quiet': [0.1, 0.1, 0.1, 0.1],
  'quietly': [0.1, 0.1, 0.1, 0.1],
  'silent': [0.1, 0.1, 0.1, 0.1],
  'silently': [0.1, 0.1, 0.1, 0.1],
  'loud': [0.1, 0.1, 0.1, 0.1],
  'loudly': [0.1, 0.1, 0.1, 0.1],
  'noisy': [0.1, 0.1, 0.1, 0.1],
  'noisily': [0.1, 0.1, 0.1, 0.1],
  'busy': [0.1, 0.1, 0.1, 0.1],
  'busily': [0.1, 0.1, 0.1, 0.1],
  'active': [0.1, 0.1, 0.1, 0.1],
  'actively': [0.1, 0.1, 0.1, 0.1],
  'lazy': [0.1, 0.1, 0.1, 0.1],
  'lazily': [0.1, 0.1, 0.1, 0.1],
  'energetic': [0.1, 0.1, 0.1, 0.1],
  'tired': [0.1, 0.1, 0.1, 0.1],
  'exhausted': [0.1, 0.1, 0.1, 0.1],
  'sleepy': [0.1, 0.1, 0.1, 0.1],
  'awake': [0.1, 0.1, 0.1, 0.1],
  'alert': [0.1, 0.1, 0.1, 0.1],
  'focused': [0.1, 0.1, 0.1, 0.1],
  'concentrated': [0.1, 0.1, 0.1, 0.1],
  'distracted': [0.1, 0.1, 0.1, 0.1],
  'confused': [0.1, 0.1, 0.1, 0.1],
  'clear': [0.1, 0.1, 0.1, 0.1],
  'clearly': [0.1, 0.1, 0.1, 0.1],
  'obvious': [0.1, 0.1, 0.1, 0.1],
  'obviously': [0.1, 0.1, 0.1, 0.1],
  'hidden': [0.1, 0.1, 0.1, 0.1],
  'secret': [0.1, 0.1, 0.1, 0.1],
  'secretly': [0.1, 0.1, 0.1, 0.1],
  'public': [0.1, 0.1, 0.1, 0.1],
  'publicly': [0.1, 0.1, 0.1, 0.1],
  'private': [0.1, 0.1, 0.1, 0.1],
  'privately': [0.1, 0.1, 0.1, 0.1],
  'personal': [0.1, 0.1, 0.1, 0.1],
  'personally': [0.1, 0.1, 0.1, 0.1],
  'individual': [0.1, 0.1, 0.1, 0.1],
  'individually': [0.1, 0.1, 0.1, 0.1],
  'group': [0.1, 0.1, 0.1, 0.1],
  'team': [0.1, 0.1, 0.1, 0.1],
  'family': [0.1, 0.1, 0.1, 0.1],
  'friends': [0.1, 0.1, 0.1, 0.1],
  'community': [0.1, 0.1, 0.1, 0.1],
  'society': [0.1, 0.1, 0.1, 0.1],
  'world': [0.1, 0.1, 0.1, 0.1],
  'earth': [0.1, 0.1, 0.1, 0.1],
  'planet': [0.1, 0.1, 0.1, 0.1],
  'country': [0.1, 0.1, 0.1, 0.1],
  'nation': [0.1, 0.1, 0.1, 0.1],
  'city': [0.1, 0.1, 0.1, 0.1],
  'town': [0.1, 0.1, 0.1, 0.1],
  'village': [0.1, 0.1, 0.1, 0.1],
  'home': [0.1, 0.1, 0.1, 0.1],
  'house': [0.1, 0.1, 0.1, 0.1],
  'school': [0.1, 0.1, 0.1, 0.1],
  'hospital': [0.1, 0.1, 0.1, 0.1],
  'library': [0.1, 0.1, 0.1, 0.1],
  'museum': [0.1, 0.1, 0.1, 0.1],
  'park': [0.1, 0.1, 0.1, 0.1],
  'garden': [0.1, 0.1, 0.1, 0.1],
  'forest': [0.1, 0.1, 0.1, 0.1],
  'mountain': [0.1, 0.1, 0.1, 0.1],
  'river': [0.1, 0.1, 0.1, 0.1],
  'lake': [0.1, 0.1, 0.1, 0.1],
  'ocean': [0.1, 0.1, 0.1, 0.1],
  'sea': [0.1, 0.1, 0.1, 0.1],
  'beach': [0.1, 0.1, 0.1, 0.1],
  'desert': [0.1, 0.1, 0.1, 0.1],
  'island': [0.1, 0.1, 0.1, 0.1],
  'bridge': [0.1, 0.1, 0.1, 0.1],
  'road': [0.1, 0.1, 0.1, 0.1],
  'street': [0.1, 0.1, 0.1, 0.1],
  'path': [0.1, 0.1, 0.1, 0.1],
  'way': [0.1, 0.1, 0.1, 0.1],
  'direction': [0.1, 0.1, 0.1, 0.1],
  'north': [0.1, 0.1, 0.1, 0.1],
  'south': [0.1, 0.1, 0.1, 0.1],
  'east': [0.1, 0.1, 0.1, 0.1],
  'west': [0.1, 0.1, 0.1, 0.1],
  'up': [0.1, 0.1, 0.1, 0.1],
  'down': [0.1, 0.1, 0.1, 0.1],
  'left': [0.1, 0.1, 0.1, 0.1],
  'right': [0.1, 0.1, 0.1, 0.1],
  'front': [0.1, 0.1, 0.1, 0.1],
  'back': [0.1, 0.1, 0.1, 0.1],
  'inside': [0.1, 0.1, 0.1, 0.1],
  'outside': [0.1, 0.1, 0.1, 0.1],
  'top': [0.1, 0.1, 0.1, 0.1],
  'bottom': [0.1, 0.1, 0.1, 0.1],
  'middle': [0.1, 0.1, 0.1, 0.1],
  'center': [0.1, 0.1, 0.1, 0.1],
  'edge': [0.1, 0.1, 0.1, 0.1],
  'corner': [0.1, 0.1, 0.1, 0.1],
  'side': [0.1, 0.1, 0.1, 0.1],
  'sprint': [0.1, 0.1, 0.1, 0.1],
  'stroll': [0.1, 0.1, 0.1, 0.1],
  'saunter': [0.1, 0.1, 0.1, 0.1]
};

// Calculate cosine similarity between two vectors
function cosineSimilarity(a: number[], b: number[]): number {
  if (a.length !== b.length) return 0;

  let dotProduct = 0;
  let normA = 0;
  let normB = 0;

  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }

  if (normA === 0 || normB === 0) return 0;

  return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
}

// Get word embedding for a word (with fallback for unknown words)
function getWordEmbedding(word: string): number[] {
  const normalizedWord = word.toLowerCase().trim();

  // Check if we have an embedding for this word
  if (WORD_EMBEDDINGS[normalizedWord]) {
    return WORD_EMBEDDINGS[normalizedWord];
  }

  // Check for partial matches (for compound words)
  for (const [key, embedding] of Object.entries(WORD_EMBEDDINGS)) {
    if (normalizedWord.includes(key) || key.includes(normalizedWord)) {
      return embedding;
    }
  }

  // Fallback: return a neutral embedding
  return [0, 0, 0, 0];
}

// Calculate semantic similarity between two texts
function calculateSemanticSimilarity(text1: string, text2: string): number {
  const words1 = text1.toLowerCase().split(/\s+/).filter(w => w.length > 0);
  const words2 = text2.toLowerCase().split(/\s+/).filter(w => w.length > 0);

  if (words1.length === 0 || words2.length === 0) return 0;

  // Get embeddings for all words
  const embeddings1 = words1.map(getWordEmbedding);
  const embeddings2 = words2.map(getWordEmbedding);

  // Calculate average embedding for each text
  const avgEmbedding1 = embeddings1.reduce((acc, emb) =>
    acc.map((val, i) => val + emb[i]), new Array(4).fill(0)
  ).map(val => val / embeddings1.length);

  const avgEmbedding2 = embeddings2.reduce((acc, emb) =>
    acc.map((val, i) => val + emb[i]), new Array(4).fill(0)
  ).map(val => val / embeddings2.length);

  return cosineSimilarity(avgEmbedding1, avgEmbedding2);
}

// Check if two texts are semantically similar
export function isSemanticallySimilar(text1: string, text2: string, threshold: number = 0.7): { isSimilar: boolean; confidence: number } {
  const similarity = calculateSemanticSimilarity(text1, text2);
  return {
    isSimilar: similarity >= threshold,
    confidence: similarity
  };
}

// Calculate Levenshtein distance between two strings
function levenshteinDistance(str1: string, str2: string): number {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));

  for (let i = 0; i <= str1.length; i++) {
    matrix[0][i] = i;
  }

  for (let j = 0; j <= str2.length; j++) {
    matrix[j][0] = j;
  }

  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // deletion
        matrix[j - 1][i] + 1, // insertion
        matrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }

  return matrix[str2.length][str1.length];
}

// Generate common variations of a word for typo checking
function generateVariations(word: string): string[] {
  const variations: string[] = [];
  const normalized = word.toLowerCase().trim();

  // Add the original word
  variations.push(normalized);

  // Common typo patterns
  const patterns = [
    // Double letters
    normalized.replace(/(.)\1/g, '$1'), // Remove double letters
    normalized.replace(/(.)/g, '$1$1'), // Add double letters

    // Common substitutions
    normalized.replace(/ph/g, 'f'),
    normalized.replace(/f/g, 'ph'),
    normalized.replace(/c/g, 'k'),
    normalized.replace(/k/g, 'c'),
    normalized.replace(/s/g, 'z'),
    normalized.replace(/z/g, 's'),

    // Vowel substitutions
    normalized.replace(/a/g, 'e'),
    normalized.replace(/e/g, 'a'),
    normalized.replace(/i/g, 'o'),
    normalized.replace(/o/g, 'i'),
    normalized.replace(/u/g, 'y'),
    normalized.replace(/y/g, 'u'),
  ];

  variations.push(...patterns);

  // Remove duplicates
  return Array.from(new Set(variations));
}

/** Canonical form for grammar answer comparison (case already lowered). */
export function normalizeGrammarAnswer(text: string): string {
  return text
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\bcan\s+not\b/g, 'cannot')
    .replace(/\bcan't\b/g, 'cannot');
}

/** Human-readable list of accepted answers for feedback messages. */
export function formatAcceptedAnswers(answers: string[]): string {
  if (answers.length === 0) return '';
  if (answers.length === 1) return answers[0];
  if (answers.length === 2) return `${answers[0]} or ${answers[1]}`;
  return `${answers.slice(0, -1).join(', ')}, or ${answers[answers.length - 1]}`;
}

// Enhanced answer validation for reading tasks
export async function validateReadingAnswer(
  userAnswer: string,
  correctAnswers: string[],
  questionType: 'factual' | 'inferential' | 'analytical' = 'factual',
  isGrammarExercise: boolean = false
): Promise<ValidationResult> {
  const normalizedUserAnswer = userAnswer.toLowerCase().trim();
  const normalizedCorrectAnswers = correctAnswers.map(ans => ans.toLowerCase().trim());
  const acceptedAnswersLabel = formatAcceptedAnswers(correctAnswers);

  // Helper to remove articles for looser comparison
  const removeArticles = (str: string) => str.replace(/\b(a|an|the)\b/g, '').trim();
  const cleanUserAnswer = removeArticles(normalizedUserAnswer);

  // --- STRICT LOGIC FOR FACTUAL GRAMMAR QUESTIONS ---
  if (questionType === 'factual' && isGrammarExercise) {
    // 0. Pre-normalization for contractions and punctuation
    const expandContractions = (text: string): string => {
      const contractions: { [key: string]: string } = {
        "don't": "do not", "doesn't": "does not", "didn't": "did not",
        "isn't": "is not", "aren't": "are not", "wasn't": "was not", "weren't": "were not",
        "haven't": "have not", "hasn't": "has not", "hadn't": "had not",
        "won't": "will not", "wouldn't": "would not", "can't": "cannot", "couldn't": "could not",
        "shouldn't": "should not", "mustn't": "must not",
        "i'm": "i am", "you're": "you are", "he's": "he is", "she's": "she is", "it's": "it is",
        "we're": "we are", "they're": "they are",
        "i've": "i have", "you've": "you have", "we've": "we have", "they've": "they have",
        "i'd": "i had", "you'd": "you had", "he'd": "he had", "she'd": "she had", "we'd": "we had", "they'd": "they had",
        "i'll": "i will", "you'll": "you will", "he'll": "he will", "she'll": "she will", "we'll": "we will", "they'll": "they will"
      };

      let expanded = text;
      // Replace contractions that are separate words or at ends of strings
      for (const [contraction, expansion] of Object.entries(contractions)) {
        // Use a regex to match the contraction as a whole word
        const regex = new RegExp(`\\b${contraction}\\b`, 'g');
        expanded = expanded.replace(regex, expansion);
        // Also handle cases where the contraction might be part of the input without word boundaries if strictly needed,
        // but word boundaries are safer to avoid false positives.
        // For grammar exercises, inputs are usually short phrases, so simple replacement works well.
      }
      return expanded.replace(/[.,;!?]$/, ''); // Remove trailing punctuation
    };

    const expandedUserAnswer = normalizeGrammarAnswer(expandContractions(normalizedUserAnswer));

    // 1. Check for matches (exact or expanded)
    for (const correctAnswer of normalizedCorrectAnswers) {
      const expandedCorrectAnswer = normalizeGrammarAnswer(expandContractions(correctAnswer));

      // Check exact match OR expanded match
      if (
        normalizeGrammarAnswer(normalizedUserAnswer) === normalizeGrammarAnswer(correctAnswer) ||
        expandedUserAnswer === expandedCorrectAnswer
      ) {
        return {
          isCorrect: true,
          confidence: 1.0,
          partialCredit: 1.0,
          suggestions: [],
          feedback: 'Perfect! Your answer is correct.',
          correctAnswer: correctAnswers[0],
          explanation: 'Your answer matches the correct answer.'
        };
      }
    }

    // 2. Check for common typos that add extra characters
    const commonExtraPatterns = [
      /ingg$/, // double g after ing (like "playingg")
      /edd$/, // double d after ed
      /ss$/, // double s (like "speakss")
      /err$/, // double r after er
      /ll$/, // double l
      /nn$/, // double n
      /tt$/, // double t
      /yy$/, // double y
      /ee$/, // double e
      /oo$/, // double o
    ];

    if (commonExtraPatterns.some(pattern => pattern.test(normalizedUserAnswer))) {
      return {
        isCorrect: false,
        confidence: 0.1,
        partialCredit: 0.1,
        suggestions: normalizedCorrectAnswers,
        feedback: `Check your spelling. Avoid common typos like double letters.`,
        correctAnswer: correctAnswers[0],
        explanation: `Your answer contains a common typo pattern. Accepted answer(s): "${acceptedAnswersLabel}"`
      };
    }

    // 3. Check if user answer is a valid English word/phrase
    try {
      const isValidWord = await dictionaryService.isValidWord(normalizedUserAnswer);
      if (!isValidWord) {
        // Check for close misspellings of valid words
        for (const correctAnswer of normalizedCorrectAnswers) {
          const distance = levenshteinDistance(normalizedUserAnswer, correctAnswer);
          const maxDistance = Math.max(1, Math.floor(correctAnswer.length * 0.3));

          if (distance <= maxDistance && distance > 0) {
            return {
              isCorrect: false,
              confidence: 0.3,
              partialCredit: 0.3,
              suggestions: [correctAnswer],
              feedback: `Close! Check your spelling.`,
              correctAnswer: correctAnswers[0],
              explanation: `You're close, but there's a spelling error. The correct answer is: "${correctAnswer}"`
            };
          }
        }
        // If not a valid word and not a close misspelling, it's just wrong
        return {
          isCorrect: false,
          confidence: 0.0,
          partialCredit: 0.0,
          suggestions: normalizedCorrectAnswers,
          feedback: `"${userAnswer}" is not a recognized English word or phrase.`,
          correctAnswer: correctAnswers[0],
          explanation: `The word or phrase you entered is not recognized as a valid English word. Accepted answer(s): "${acceptedAnswersLabel}"`
        };
      }
    } catch (error) {
      console.warn('WordNet validation failed:', error);
    }

    // 4. If it's a valid word but not exact match, check for close Levenshtein distance
    for (const correctAnswer of normalizedCorrectAnswers) {
      const distance = levenshteinDistance(normalizedUserAnswer, correctAnswer);
      const maxDistance = Math.max(1, Math.floor(correctAnswer.length * 0.1)); // Stricter 10% threshold

      if (distance <= maxDistance && distance > 0) {
        return {
          isCorrect: false,
          confidence: 0.4,
          partialCredit: 0.4,
          suggestions: [correctAnswer],
          feedback: `Close! Check your spelling.`,
          correctAnswer: correctAnswers[0],
          explanation: `You're very close, but there's a minor spelling error. The correct answer is: "${correctAnswer}"`
        };
      }
    }

    // 5. If it's a factual question and it's not an exact match, not a common typo,
    // not an invalid word, and not a close misspelling, then it's incorrect.
    return {
      isCorrect: false,
      confidence: 0.0,
      partialCredit: 0.0,
      suggestions: normalizedCorrectAnswers,
      feedback: `Your answer is incorrect.`,
      correctAnswer: correctAnswers[0],
      explanation: `Accepted answer(s): "${acceptedAnswersLabel}"`
    };
  }

  // --- ORIGINAL LOGIC FOR NON-FACTUAL QUESTIONS ---
  // 1. Check for exact matches
  for (const correctAnswer of normalizedCorrectAnswers) {
    if (normalizedUserAnswer === correctAnswer) {
      return {
        isCorrect: true,
        confidence: 1.0,
        partialCredit: 1.0,
        suggestions: [],
        feedback: 'Perfect! Your answer is exactly right.',
        correctAnswer: correctAnswers[0],
        explanation: 'Your answer matches the correct answer perfectly.'
      };
    }
  }

  // 2. Check for partial matches (only for non-grammar exercises)
  if (!isGrammarExercise) {
    for (const correctAnswer of normalizedCorrectAnswers) {
      // For reading comprehension, use the original logic
      // Only accept if user answer contains the complete correct answer, not the other way around
      // This prevents "g" from matching "go" but allows "go to school" to match "go"
      if (normalizedUserAnswer.includes(correctAnswer) && normalizedUserAnswer.length >= correctAnswer.length) {
        // For multi-word answers (like "is working"), require the answer to be much more complete
        if (correctAnswer.includes(' ') && normalizedUserAnswer.length < correctAnswer.length * 0.8) {
          // Skip partial matches for multi-word answers if less than 80% complete
          continue;
        }
        return {
          isCorrect: true,
          confidence: 0.9,
          partialCredit: 0.9,
          suggestions: [],
          feedback: 'Great! Your answer contains the correct information.',
          correctAnswer: correctAnswers[0],
          explanation: 'Your answer includes the key information from the correct answer.'
        };
      }
    }
  }
  // For grammar exercises, skip partial matching entirely - only exact matches are allowed

  // 3. Fuzzy matching for reading comprehension (semantic similarity)
  for (const correctAnswer of normalizedCorrectAnswers) {
    // Check if user answer contains key words from correct answer
    const correctWords = correctAnswer.split(/\s+/).filter(w => w.length > 2);
    const userWords = normalizedUserAnswer.split(/\s+/).filter(w => w.length > 2);

    // Calculate word overlap
    const matchingWords = correctWords.filter(word =>
      userWords.some(userWord =>
        userWord.includes(word) || word.includes(userWord) ||
        calculateSemanticSimilarity(userWord, word) > 0.7
      )
    );

    // If user answer contains most key words from correct answer
    if (matchingWords.length >= correctWords.length * 0.6) {
      const partialCredit = Math.min(matchingWords.length / correctWords.length, 0.8);
      return {
        isCorrect: true,
        confidence: partialCredit,
        partialCredit: partialCredit,
        suggestions: [],
        feedback: 'Good! Your answer captures the main ideas.',
        correctAnswer: correctAnswers[0],
        explanation: `Your answer includes key concepts. The complete answer is: "${correctAnswer}"`
      };
    }

    // NEW: Check for match without articles (e.g. "hearing" vs "the hearing")
    const cleanCorrectAnswer = removeArticles(correctAnswer);
    if (cleanUserAnswer === cleanCorrectAnswer && cleanUserAnswer.length > 2) {
      return {
        isCorrect: true,
        confidence: 0.95,
        partialCredit: 1.0,
        suggestions: [],
        feedback: 'Correct!',
        correctAnswer: correctAnswers[0],
        explanation: 'Your answer is correct.'
      };
    }
  }

  // 4. Check for semantic similarity (disabled for grammar exercises which need exact answers)
  if (!isGrammarExercise) {
    let bestSimilarity = 0;
    let bestMatch = '';

    for (const correctAnswer of normalizedCorrectAnswers) {
      const similarity = calculateSemanticSimilarity(normalizedUserAnswer, correctAnswer);
      if (similarity > bestSimilarity) {
        bestSimilarity = similarity;
        bestMatch = correctAnswer;
      }
    }

    // Adjust threshold based on question type
    const threshold = questionType === 'inferential' ? 0.6 : 0.5;

    if (bestSimilarity >= threshold) {
      const partialCredit = Math.min(bestSimilarity, 0.8); // Cap partial credit at 0.8 for semantic matches
      return {
        isCorrect: true,
        confidence: partialCredit,
        partialCredit: partialCredit,
        suggestions: [],
        feedback: 'Good! Your answer is semantically similar to the correct answer.',
        correctAnswer: correctAnswers[0],
        explanation: `Your answer is similar to: "${bestMatch}"`
      };
    }
  }

  // 5. Check for common variations and typos (disabled for grammar exercises)
  if (!isGrammarExercise) {
    const variations = generateVariations(normalizedUserAnswer);
    for (const variation of variations) {
      for (const correctAnswer of normalizedCorrectAnswers) {
        if (variation === correctAnswer) {
          return {
            isCorrect: true,
            confidence: 0.8,
            partialCredit: 0.8,
            suggestions: [],
            feedback: `Good! You had the right idea.`,
            correctAnswer: correctAnswers[0],
            explanation: `The correct answer is: "${correctAnswer}"`
          };
        }
      }
    }
  }

  // 6. Default to incorrect if no other conditions met
  return {
    isCorrect: false,
    confidence: 0.0,
    partialCredit: 0.0,
    suggestions: normalizedCorrectAnswers,
    feedback: 'Your answer is incorrect.',
    correctAnswer: correctAnswers[0],
    explanation: `The correct answer is: "${correctAnswers[0]}"`
  };
}


// Enhanced writing assessment validation
export function validateWritingContent(
  text: string,
  taskType: string,
  criteria: {
    minLength?: number;
    maxLength?: number;
    requiredElements?: string[];
    forbiddenWords?: string[];
  } = {}
): ValidationResult {
  const issues: string[] = [];
  const suggestions: string[] = [];
  let isCorrect = true;
  let confidence = 1.0;

  // Check length requirements
  if (criteria.minLength && text.length < criteria.minLength) {
    issues.push(`Text is too short. Minimum length: ${criteria.minLength} characters`);
    isCorrect = false;
    confidence -= 0.2;
  }

  if (criteria.maxLength && text.length > criteria.maxLength) {
    issues.push(`Text is too long. Maximum length: ${criteria.maxLength} characters`);
    isCorrect = false;
    confidence -= 0.1;
  }

  // Check for required elements
  if (criteria.requiredElements) {
    for (const element of criteria.requiredElements) {
      if (!text.toLowerCase().includes(element.toLowerCase())) {
        issues.push(`Missing required element: "${element}"`);
        isCorrect = false;
        confidence -= 0.15;
      }
    }
  }

  // Check for forbidden words
  if (criteria.forbiddenWords) {
    for (const word of criteria.forbiddenWords) {
      if (text.toLowerCase().includes(word.toLowerCase())) {
        issues.push(`Contains forbidden word: "${word}"`);
        isCorrect = false;
        confidence -= 0.1;
      }
    }
  }

  // Basic grammar and style checks
  const writingIssues = detectWritingIssues(text);
  if (writingIssues.length > 0) {
    suggestions.push(...writingIssues);
    confidence -= writingIssues.length * 0.05;
  }

  // Ensure confidence doesn't go below 0
  confidence = Math.max(0, confidence);

  return {
    isCorrect,
    confidence,
    suggestions,
    feedback: issues.length > 0 ? issues.join('; ') : 'Good work!'
  };
}

// Check for common writing issues
export function detectWritingIssues(text: string): string[] {
  const issues: string[] = [];

  // Check for run-on sentences
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
  for (const sentence of sentences) {
    const words = sentence.trim().split(/\s+/);
    if (words.length > 40) {
      issues.push('Consider breaking up long sentences for better readability');
    }
  }

  // Check for repetitive words
  const words = text.toLowerCase().split(/\s+/);
  const wordCount: { [key: string]: number } = {};
  words.forEach(word => {
    wordCount[word] = (wordCount[word] || 0) + 1;
  });

  for (const [word, count] of Object.entries(wordCount)) {
    if (count > 3 && word.length > 3) {
      issues.push(`Consider using synonyms for "${word}" (used ${count} times)`);
    }
  }

  // Check for basic grammar issues
  if (text.includes('  ')) {
    issues.push('Remove extra spaces between words');
  }

  if (!text.match(/[.!?]$/)) {
    issues.push('Consider adding proper punctuation at the end');
  }

  return issues;
}
